import { useCallback, useMemo } from 'react'
import { useTranslations } from 'next-intl'
import type { SurveyConfig } from './types'

const SURVEYS_CONFIG = {
  dashboard_feedback_20250119: {
    id: 'dashboard_feedback_20250119',
    question: 'How satisfied are you with the coach dashboard?',
    cooldownDays: 30,
    triggers: ['visit_count'] as const,
  },
  phone_check_ease_2w_20250924: {
    id: 'phone_check_ease_2w_20250924',
    question: 'On a scale of 1-5, how easy or difficult is it to check your schedule on your phone today?',
    cooldownDays: 7,
    triggers: ['two_weeks_after_first_login'] as const,
    feedbackBehavior: {
      showWhen: 'low_ratings',
    },
  },
  satisfaction_check_4w_20250924: {
    id: 'satisfaction_check_4w_20250924',
    question: 'Overall, how satisfied are you with this Coach View?',
    cooldownDays: 7,
    triggers: ['four_weeks_after_first_login'] as const,
    feedbackBehavior: {
      showWhen: 'high_and_low_ratings',
    },
  },
} satisfies Record<SurveyConfig['id'], SurveyConfig>

export type AvailableSurveyIds = keyof typeof SURVEYS_CONFIG
export type SurveyTriggers<ID extends AvailableSurveyIds> = (typeof SURVEYS_CONFIG)[ID]['triggers'][number]

type SurveysConfig = Record<
  AvailableSurveyIds,
  SurveyConfig & {
    translated_question: string
    translateFollowUpQuestion: (ratingType?: 'high' | 'low') => string
  }
>

/**
 * Returns the surveys configuration, enhancing it with the question translated to the current locale.
 * The original question is kept for tracking purposes (to avoid multiple languages in our events).
 */
export function useSurveysConfig(): SurveysConfig {
  const t = useTranslations('csat')

  /**
   * Translates the question of a CSAT survey into the current locale.
   */
  const translateSurveyQuestion = useCallback(
    (surveyId: AvailableSurveyIds) => {
      switch (surveyId) {
        case 'dashboard_feedback_20250119':
          return t('survey.dashboard_feedback_20250119.question')
        case 'phone_check_ease_2w_20250924':
          return t('survey.phone_check_ease_2w_20250924.question' as Parameters<typeof t>[0])
        case 'satisfaction_check_4w_20250924':
          return t('survey.satisfaction_check_4w_20250924.question' as Parameters<typeof t>[0])
        default:
          const _exhaustiveCheck: never = surveyId
          throw new Error(`Unknown survey ${_exhaustiveCheck}`)
      }
    },
    [t]
  )

  /**
   * Translates the follow-up question for a CSAT survey based on the survey ID and rating type.
   */
  const translateFollowUpQuestion = useCallback(
    (surveyId: AvailableSurveyIds, ratingType?: 'high' | 'low') => {
      switch (surveyId) {
        case 'dashboard_feedback_20250119':
          return t('send_us_feedback_description') // Use default for this survey
        case 'phone_check_ease_2w_20250924':
          return t('followup.phone_check_ease_2w_20250924.question' as Parameters<typeof t>[0])
        case 'satisfaction_check_4w_20250924':
          if (ratingType === 'high') {
            return t('followup.satisfaction_check_4w_20250924.high_rating_question' as Parameters<typeof t>[0])
          } else {
            return t('followup.satisfaction_check_4w_20250924.low_rating_question' as Parameters<typeof t>[0])
          }
        default:
          const _exhaustiveCheck: never = surveyId
          throw new Error(`Unknown survey ${_exhaustiveCheck}`)
      }
    },
    [t]
  )

  return useMemo(() => {
    return Object.entries(SURVEYS_CONFIG).reduce((acc, [surveyId, surveyConfig]) => {
      const surveyIdFromConfig = surveyId as AvailableSurveyIds

      acc[surveyIdFromConfig] = {
        ...surveyConfig,
        translated_question: translateSurveyQuestion(surveyIdFromConfig),
        translateFollowUpQuestion: (ratingType?: 'high' | 'low') =>
          translateFollowUpQuestion(surveyIdFromConfig, ratingType),
      }

      return acc
    }, {} as SurveysConfig)
  }, [translateSurveyQuestion, translateFollowUpQuestion])
}

export { SURVEYS_CONFIG }
